/**
 * `draft-dismissal`: a firm hides one of its own unclaimed prospect drafts from the
 * Prospects list (crossmarc/steward-app#1570). See that issue for the full report: a
 * DRAFT (an intake started but never submitted) has no detail page and no way to be
 * archived or removed today, so it just sits in the list until the retention sweeper
 * expires it — which #1414 records as unset in every environment, so in practice that
 * never happens.
 *
 * ## Why a new row, and not a field on `intake-draft`
 *
 * `intake-draft` (`intakeDraft.ts`, in `STEWARD_ADVISOR_FIRM_TYPE_DEFS`) is written ONLY
 * by the intake-writer and intake-reader background Lambdas, over the IAM (SigV4)
 * `/system/*` plane, per this repo's appcore-docs/26-workstream-D invariant (see this
 * repo's root CLAUDE.md). The intake-reader's session policy is scoped to exactly ONE
 * appcore resource — `GET` on `intake-draft/*` — and the intake-writer's own scope has no
 * firm-initiated write path at all (`services/intake-reader/src/scopedCredentials.ts`,
 * `services/intake-writer/src/scopedCredentials.ts`). "The firm dismisses a prospect from
 * its own list" is a firm-plane action with no client activity behind it; making it write
 * `intake-draft` would mean either widening a background role's IAM to accept a
 * firm-initiated write it was never scoped for, or routing the write through the
 * background writer for a request it did not originate — both rejected on #1570 as the
 * wrong shape, for the same reason `intakeInvitation.ts`'s docblock gives for keeping
 * lifecycle state off of a row a background role owns: it costs a property pinned across
 * two repos for a convenience that does not need it.
 *
 * This row is the alternative: the FIRM's own data about its OWN list, written by the
 * façade (`services/steward-app-api`) under the advisor's own bearer token, into the
 * firm's own workspace, exactly like `intake-invitation` and `sponsored-invitation` are.
 * Nothing about the prospect's draft row is touched; the draft keeps existing, unchanged,
 * for as long as the (currently inert) retention sweeper leaves it there. Dismissing only
 * changes what the FIRM's own list shows.
 *
 * ## One row per dismissal, not one row per draft
 *
 * appcore's `createEntity` mints its own instanceId (a ULID) and takes no caller-supplied
 * id, so this type cannot be keyed BY `draftId` the way a direct `getEntity` lookup would
 * want. Instead each dismissal is its own row carrying `draftId` as an attribute, and the
 * façade lists the firm's rows of this type and matches by that attribute — the same
 * "mint an id, filter by an attribute" shape `draftProspects()` (`handlers/advisor.ts`)
 * already uses to scope `intake-draft` rows by `firmId`. Dismissing the same draft twice
 * (a retry, or two tabs) mints a second row rather than erroring; the read side only
 * checks for AT LEAST ONE live row per `draftId`, so a duplicate is harmless, just wasted.
 *
 * ## No advisor-facing un-dismiss button (crossmarc/steward-app#1722 follow-up review, 2026-09-28)
 *
 * No route lets the advisor un-dismiss a draft directly — there is still no button, and
 * copy shown to the advisor must not promise one. But a row of this type is no longer
 * permanent: `services/steward-app-api/src/handlers/intakeInvitation.ts`'s
 * `clearDraftDismissal` deletes every row this firm holds for a `draftId` a CREATE or
 * RESEND re-invite reuses, right after that invitation's own send has succeeded — "the
 * firm asking again" is the trigger, not a button. `services/clientPurge.ts`'s
 * `purgeClientDismissals` deletes rows of this type too, for its own different trigger
 * (a client's data being removed entirely). Absent either trigger, a dismissed draft
 * stays out of the firm's list until the retention sweeper eventually removes the
 * underlying `intake-draft` row (or forever, while that sweeper stays unset, per #1414).
 */
import { z } from 'zod';
import { ULID_PATTERN } from './intakeMessage.js';

export const DRAFT_DISMISSAL_TYPE_CODE = 'draft-dismissal';

const id = z.string().min(1).max(200);
const instant = z.iso.datetime();

export const DraftDismissalAttributesSchema = z.strictObject({
  firmId: id,
  /** The `intake-draft` row's own `draftId`, the same {@link ULID_PATTERN} that type's
   *  field is bound to. Never the appcore instanceId of THIS row. */
  draftId: z.string().regex(ULID_PATTERN),
  dismissedAt: instant,
  dismissedBy: id,
});

export type DraftDismissalAttributes = z.infer<typeof DraftDismissalAttributesSchema>;

/** Reads a stored `draft-dismissal` row's attributes. Like `intakeInvitation.ts`'s reader,
 *  `unreadable` covers both "does not parse" and "is missing" — this schema describes the
 *  whole row, so a row of this type that fails to parse is just broken, not partially
 *  readable. */
export function readDraftDismissal(
  value: unknown,
): { kind: 'dismissal'; dismissal: DraftDismissalAttributes } | { kind: 'unreadable' } {
  const parsed = DraftDismissalAttributesSchema.safeParse(value);
  return parsed.success ? { kind: 'dismissal', dismissal: parsed.data } : { kind: 'unreadable' };
}
